import { t, Trans } from '@lingui/macro';
import { Field, Form } from '~/app/shared/components/Form';
import { Alert, BadgeIcon, Button, Tooltip } from '@economic/taco';
import { CountryCallingCodeDropdown } from '~/app/shared/components/ui/dropdowns/CountryCallingCodeDropdown/CountryCallingCodeDropdown';
import { refreshCurrentLegacyFrame, useHostRefetch } from '@economic/react-app';
import { COUNTRIES_BY_CODE } from '~/app/shared/util/utilities/countries';
import { usePayrollOnboardingContext } from '../../context/OnboardingContext';
import { BankAccountStepState, GetCurrentCompanyBankInformationResponse } from '../../types';
import { getBankAccountStepValidationSchema, getCreateCompanyBody } from '../../utils/utils';
import { useCreateCompany } from './useCreateCompany';

export const BankAccountStepWhenCVR = ({ bankData }: { bankData: GetCurrentCompanyBankInformationResponse }) => {
    const { companyInfoStepState, onNext, setIsDialogLoading } = usePayrollOnboardingContext();
    const { trigger: triggerCompanyCreation } = useCreateCompany();
    const refetchHost = useHostRefetch();

    const onSubmit = (bankAccountStepState: BankAccountStepState) => {
        const companyToCreate = getCreateCompanyBody(bankAccountStepState, companyInfoStepState);
        setIsDialogLoading(true);

        triggerCompanyCreation({ company: companyToCreate })
            .then(() => {
                refreshCurrentLegacyFrame();
                refetchHost();
                onNext();
            })
            .finally(() => {
                setIsDialogLoading(false);
            });
    };
    return (
        <>
            <p className="text-sm text-center">
                <Trans>
                    In order to send money on your behalf, we will request for the transfer agreement and validate the company's
                    bank account after filling this form.
                </Trans>
            </p>
            <Form
                onSubmit={onSubmit}
                initialData={{ accountNo: bankData?.bankAccount, regNo: bankData?.bankSortCode }}
                key="payroll-onboarding-step3-form"
                skipNotifications
                validationSchema={getBankAccountStepValidationSchema({ isCVR: true })}>
                <div className="flex flex-wrap">
                    <div className="w-1/2 border-r pr-3 border-grey-300">
                        <div className="flex items-center mb-3">
                            <p className="text-base font-bold w-fit mb-0 mr-2">
                                <Trans>Authorized person to sign</Trans>
                            </p>
                            <Tooltip className="m-auto" title={t`Person who can legally sign on behalf of the company.`}>
                                <BadgeIcon icon="info" color="blue" small />
                            </Tooltip>
                        </div>

                        <div>
                            <Field.Text id="contactName" name="contactName" label={t`Name`} required />
                            <div className="flex flex-wrap h-18">
                                <label className="w-full text-xs font-bold mb-1">
                                    <Trans>Phone no.</Trans> *
                                </label>
                                <div className="flex w-full">
                                    <Field
                                        name="callingCode"
                                        fieldClassName="w-2/5 mr-2"
                                        initialValue={`${COUNTRIES_BY_CODE.DK.code}#${COUNTRIES_BY_CODE.DK.callingCode}`}>
                                        {({ input }) => <CountryCallingCodeDropdown {...input} readOnly />}
                                    </Field>
                                    <Field.Text
                                        aria-label={t`Phone no.`}
                                        id="phoneNumber"
                                        name="phoneNumber"
                                        fieldClassName="w-3/5"
                                        type="text"
                                        required
                                    />
                                </div>
                            </div>
                            <Field.Text id="email" name="email" label={t`E-mail`} required />
                        </div>
                    </div>
                    <div className="w-1/2 pl-3">
                        <div className="flex items-center mb-3">
                            <p className="text-base font-bold w-fit mb-0 mr-2">
                                <Trans>New bank account</Trans>
                            </p>
                        </div>
                        <div>
                            <div className="flex flex-wrap h-18">
                                <div className="flex w-full">
                                    <Field.Text
                                        id="regNo"
                                        name="regNo"
                                        label={t`Reg no.`}
                                        required
                                        fieldClassName="w-2/5 mr-2"
                                        suggestion={bankData?.bankSortCode}
                                    />
                                    <Field.Text
                                        id="accountNo"
                                        name="accountNo"
                                        label={t`Account no.`}
                                        fieldClassName="w-3/5"
                                        type="text"
                                        required
                                        suggestion={bankData?.bankAccount}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <Alert state="warning" className="mt-2">
                        <Trans>
                            The authorized person to sign will receive an e-mail from Mastercard. After signing, this will take a
                            few days and you will be able to check the process in the system. You can always apply to change your
                            bank account from the company page.
                        </Trans>
                    </Alert>
                    <div className="flex w-full justify-end mt-6">
                        <Button type="submit" appearance="primary">
                            <Trans>Next</Trans>
                        </Button>
                    </div>
                </div>
            </Form>
        </>
    );
};
